import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Fingerprint } from "lucide-react";
import { fetchDeviceTrusted, requestDeviceApproval, withdrawDeviceApproval } from "../api/devices.js";
import type { IssuedDeviceApproval } from "../api/devices.js";
import { describeAuthError } from "../app/authFlow.js";
import { setDeviceApprovalHandler } from "../app/deviceApproval.js";
import { confirmWithPasskey, passkeyUnlockAvailable } from "../app/passkeyUnlock.js";
import { Dialog } from "../ui/Dialog.js";
import { Alert, Button, Skeleton } from "../ui/primitives.js";

const POLL_MS = 3000;

/**
 * Shown when the trusted-device policy holds back a sign-in on a new device. The device shows a
 * code to type on an already trusted device (Einstellungen → Neue Geräte freigeben), or confirms
 * itself with a passkey. Opened by the request layer on DEVICE_APPROVAL_REQUIRED.
 */
export function DeviceApprovalDialog(): ReactNode {
  const [open, setOpen] = useState(false);
  const [approval, setApproval] = useState<IssuedDeviceApproval | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const settle = useRef<((enrolled: boolean) => void) | undefined>(undefined);

  useEffect((): (() => void) => {
    const unregister = setDeviceApprovalHandler((): Promise<boolean> => new Promise<boolean>((resolve): void => {
      settle.current = resolve;
      setApproval(undefined);
      setError(undefined);
      setOpen(true);
    }));
    return (): void => {
      unregister();
      settle.current?.(false);
      settle.current = undefined;
    };
  }, []);

  // Ask for a code once the dialog opens, and again when the old one ran out.
  useEffect((): (() => void) | undefined => {
    if (!open || (approval !== undefined && approval.expiresAt > Date.now())) {
      return undefined;
    }
    let active = true;
    requestDeviceApproval()
      .then((issued: IssuedDeviceApproval): void => {
        if (active) {
          setApproval(issued);
        }
      })
      .catch((err: unknown): void => {
        if (active) {
          setError(describeAuthError(err));
        }
      });
    return (): void => {
      active = false;
    };
  }, [open, approval]);

  // The trusted device confirms elsewhere; watch for it.
  useEffect((): (() => void) | undefined => {
    if (!open) {
      return undefined;
    }
    const timer = window.setInterval((): void => {
      void fetchDeviceTrusted().then((trusted: boolean): void => {
        if (trusted) {
          close(true);
        }
      }).catch((): void => undefined);
    }, POLL_MS);
    return (): void => window.clearInterval(timer);
  }, [open]);

  function close(enrolled: boolean): void {
    settle.current?.(enrolled);
    settle.current = undefined;
    setOpen(false);
  }

  function cancel(): void {
    if (approval !== undefined) {
      void withdrawDeviceApproval(approval.id).catch((): void => undefined);
    }
    close(false);
  }

  async function withPasskey(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      // A passkey check on this device enrols it on the server.
      await confirmWithPasskey();
      close(true);
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={cancel}
      title="Neues Gerät freigeben"
      description="Dein Administrator verlangt, dass neue Geräte bestätigt werden. Öffne PasswordFlow auf einem Gerät, das du schon nutzt, und gib dort unter Einstellungen → Neue Geräte freigeben diesen Code ein."
    >
      <div className="flex flex-col gap-5 p-5">
        {approval === undefined && error === undefined ? <Skeleton className="h-16" /> : null}
        {approval !== undefined ? (
          <p className="text-center font-mono text-3xl font-semibold tracking-[0.4em] text-foam" aria-live="polite">{approval.code}</p>
        ) : null}
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-center text-xs text-mist-2">Sobald das Gerät freigegeben ist, geht es hier automatisch weiter.</p>
        {passkeyUnlockAvailable() ? (
          <Button variant="secondary" icon={Fingerprint} loading={busy} className="w-full" onClick={(): void => void withPasskey()}>
            Stattdessen mit Passkey bestätigen
          </Button>
        ) : null}
        <Button variant="ghost" className="w-full" onClick={cancel}>Abbrechen</Button>
      </div>
    </Dialog>
  );
}
